import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import type { User } from "@domaddress/shared";
import { canViewActivityLog } from "@domaddress/shared";

type PendingRow = {
  id: number;
  contact_id: number | null;
  mailerlite_subscriber_id: string;
  ml_email: string | null;
  contact_name?: string;
  proposed: Record<string, unknown>;
  baseline: Record<string, unknown>;
  status: string;
  created_at: string;
  updated_at: string;
};

function FieldDiff({
  label,
  before,
  after,
}: {
  label: string;
  before: string;
  after: string;
}) {
  if (before === after) return null;
  return (
    <tr>
      <td style={{ padding: "0.35rem 0.75rem 0.35rem 0", verticalAlign: "top", fontWeight: 500 }}>{label}</td>
      <td style={{ padding: "0.35rem 0", color: "var(--muted, #666)" }}>{before || "—"}</td>
      <td style={{ padding: "0.35rem 0 0.35rem 0.75rem" }}>{after || "—"}</td>
    </tr>
  );
}

export default function MailerLiteInboxPage() {
  const [me, setMe] = useState<User | null>(null);
  const [rows, setRows] = useState<PendingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [meRes, listRes] = await Promise.all([
        api.get<{ data: User }>("/api/me"),
        api.get<{ data: PendingRow[] }>("/api/mailerlite/inbound-pending"),
      ]);
      setMe(meRes.data.data);
      setRows(listRes.data.data ?? []);
    } catch {
      setErr("Kon de wachtrij niet laden.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (me && !canViewActivityLog(me)) {
    return (
      <div className="page">
        <p className="page-subtitle">Geen toegang tot deze pagina.</p>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">MailerLite: wijzigingen goedkeuren</h1>
      <p className="page-subtitle">
        Wanneer iemand contactgegevens in MailerLite aanpast, verschijnt het voorstel hier. Lijst‑inschrijvingen gaan
        direct mee; alleen naam/e‑mail/telefoon wacht op jouw akkoord.
      </p>
      {msg && (
        <p className="form-hint" style={{ marginBottom: "1rem" }}>
          {msg}
        </p>
      )}
      {err && <p className="form-error">{err}</p>}
      {loading ? (
        <p>Bezig met laden…</p>
      ) : rows.length === 0 ? (
        <p>Geen openstaande voorstellen.</p>
      ) : (
        <ul className="data-list" style={{ listStyle: "none", padding: 0 }}>
          {rows.map((r) => (
            <li
              key={r.id}
              className="data-list-row"
              style={{ flexDirection: "column", alignItems: "stretch", gap: "0.75rem" }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "baseline" }}>
                <strong>{r.contact_name || `Contact #${r.contact_id ?? "?"}`}</strong>
                <span className="data-list-row-muted">MailerLite: {r.ml_email || r.mailerlite_subscriber_id}</span>
                <span className="data-list-row-muted">Aangepast {new Date(r.updated_at).toLocaleString("nl-NL")}</span>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Veld</th>
                    <th style={{ textAlign: "left" }}>Nu in DomAddress</th>
                    <th style={{ textAlign: "left" }}>Voorstel (MailerLite)</th>
                  </tr>
                </thead>
                <tbody>
                  <FieldDiff
                    label="Voornaam"
                    before={String(r.baseline.first_name ?? "")}
                    after={String(r.proposed.first_name ?? r.baseline.first_name ?? "")}
                  />
                  <FieldDiff
                    label="Achternaam"
                    before={String(r.baseline.last_name ?? "")}
                    after={String(r.proposed.last_name ?? r.baseline.last_name ?? "")}
                  />
                  <FieldDiff
                    label="E-mail"
                    before={String(r.baseline.email_prive ?? "")}
                    after={String(r.proposed.email_prive ?? r.baseline.email_prive ?? "")}
                  />
                  <FieldDiff
                    label="Telefoon"
                    before={String(r.baseline.phone ?? "")}
                    after={String(r.proposed.phone ?? r.baseline.phone ?? "")}
                  />
                </tbody>
              </table>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={busyId === r.id}
                  onClick={async () => {
                    setBusyId(r.id);
                    setMsg("");
                    try {
                      await api.post(`/api/mailerlite/inbound-pending/${r.id}/approve`, {});
                      setMsg("Goedgekeurd en opgeslagen.");
                      await load();
                    } catch (e: unknown) {
                      const ax = e as { response?: { data?: { error?: string } } };
                      setMsg(ax.response?.data?.error || "Goedkeuren mislukt.");
                    } finally {
                      setBusyId(null);
                    }
                  }}
                >
                  Goedkeuren
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={busyId === r.id}
                  onClick={async () => {
                    const reason = window.prompt("Optionele reden voor afwijzen (mag leeg):") ?? "";
                    setBusyId(r.id);
                    setMsg("");
                    try {
                      await api.post(`/api/mailerlite/inbound-pending/${r.id}/reject`, {
                        reason: reason.trim() || undefined,
                      });
                      setMsg("Afgewezen.");
                      await load();
                    } catch (e: unknown) {
                      const ax = e as { response?: { data?: { error?: string } } };
                      setMsg(ax.response?.data?.error || "Afwijzen mislukt.");
                    } finally {
                      setBusyId(null);
                    }
                  }}
                >
                  Afwijzen
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p style={{ marginTop: "2rem" }}>
        <button type="button" className="btn-secondary" onClick={() => void load()}>
          Vernieuwen
        </button>
      </p>
    </div>
  );
}
